import React, { useState, useEffect, useLayoutEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { API_CONFIG } from '../config/api';

const PaymentResult = ({ orderId: propOrderId, onReturnHome, embedded = false }) => {
  const [searchParams] = useSearchParams();
  const canSellParam = searchParams.get('can_sell');
  const [orderStatus, setOrderStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryTrigger, setRetryTrigger] = useState(0);
  const [messengerConfig, setMessengerConfig] = useState(null);
  const hasCheckedStatus = React.useRef(null);
  const lastCheckedOrderId = React.useRef(null);
  const isCheckingStatus = React.useRef(false);
  const checkStatusAbortController = React.useRef(null);
  const pollingIntervalRef = React.useRef(null);
  const isPollingRef = React.useRef(false);
  const hasNotifiedParentRef = React.useRef(false);
  const hasClosedPopupRef = React.useRef(false);
  const checkPaymentStatusRef = React.useRef(null);
  const retryTriggerRef = React.useRef(0);
  const isReturningFromPaymentRef = React.useRef(false);
  
  const isInPopup = typeof window !== 'undefined' && window.opener !== null && window.opener !== window;
  
  const orderId = useMemo(() => {
    if (propOrderId) return propOrderId;

    // Check standard URL parameters
    let id = searchParams.get('order_id') ||
             searchParams.get('OrderId') ||
             searchParams.get('OrderNumber') || // AssetPayments common param
             searchParams.get('merchant_internal_order_id'); // AssetPayments common param

    // Check for 'data' param (LiqPay style)
    if (!id) {
      const dataParam = searchParams.get('data');
      if (dataParam) {
        try {
          const decodedData = JSON.parse(atob(dataParam));
          id = decodedData.order_id || decodedData.order_id;
        } catch (e) {}
        }
      }

    // Fallback to localStorage (Robust for redirects that strip params)
    if (!id && typeof window !== 'undefined') {
      id = localStorage.getItem('currentOrderId');
    }
    return id;
  }, [searchParams, propOrderId]);
  
  useLayoutEffect(() => {
    if (isInPopup && orderId && !hasClosedPopupRef.current) {
      hasClosedPopupRef.current = true;
      
      try {
        if (window.opener && window.opener.parent && window.opener.parent !== window.opener) {
          window.opener.parent.postMessage({
            type: 'show-confirmation',
            orderId: orderId
          }, '*');
        } else if (window.opener) {
          window.opener.postMessage({
            type: 'show-confirmation',
            orderId: orderId
          }, '*');
        }
      } catch (e) {
        console.warn('Failed to send postMessage to parent window:', e);
      }
      
      requestAnimationFrame(() => {
        try {
          window.close();
        } catch (e) {
          console.warn('Failed to close popup window:', e);
        }
      });
    }
  }, [isInPopup, orderId]);

  // Fetch messenger configuration
  useEffect(() => {
    const fetchMessengerConfig = async () => {
      try {
        const response = await fetch(`${API_CONFIG.baseURL}/api/messengers/config`);
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.hasAnyMessenger) {
            setMessengerConfig(data.config);
          }
        }
      } catch (error) {
        console.error('Failed to fetch messenger config:', error);
        // Don't show error to user, just continue without bot links
      }
    };

    fetchMessengerConfig();
  }, []);

  useEffect(() => {
    if (isInPopup) {
      return;
    }
    
    if (!orderId) {
      setError('Order ID not found. Please try again or contact support.');
      setLoading(false);
      return;
    }
    
    // Get order_id from URL (ignoring _t parameter used for iframe reload)
    const urlOrderId = searchParams.get('order_id');
    // If orderId comes from prop (via postMessage), it's always a return from payment
    // This ensures fresh status check when user clicks "back to application"
    // Also check if isReturningFromPaymentRef is already set (from window close or retry)
    const isReturningFromPayment = urlOrderId !== null || 
                                   (propOrderId !== undefined && propOrderId !== null) ||
                                   isReturningFromPaymentRef.current === true;
    
    // Mark as returning from payment if propOrderId is provided or ref is already set
    if (propOrderId !== undefined && propOrderId !== null) {
      isReturningFromPaymentRef.current = true;
    } else if (urlOrderId !== null) {
      isReturningFromPaymentRef.current = true;
    }
    // If ref is already set (from window close or retry), keep it true
    
    if (isReturningFromPayment) {
      // Clear all polling state to force fresh start
      hasCheckedStatus.current = null;
      lastCheckedOrderId.current = null;
      
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
        isPollingRef.current = false;
      }
      
      if (checkStatusAbortController.current) {
        checkStatusAbortController.current.abort();
      }
      
      // When returning from payment (via back link or window close), show loader immediately
      // This ensures consistent UX - loader is always shown when returning from payment
      setError(null);
      // Always set loading to true when returning from payment to show loader immediately
      // This matches the behavior when returning via "back link"
      setLoading(true);
      isCheckingStatus.current = false;
      
      // Note: checkPaymentStatus() will be called at the end of useEffect
      // which will restart polling automatically
    }
    
    const isNewPaymentAttempt = lastCheckedOrderId.current !== null && lastCheckedOrderId.current !== orderId;
    
    if (isNewPaymentAttempt) {
      hasCheckedStatus.current = null;
      setOrderStatus(null);
      setLoading(true);
    }
    
    // Check if we should skip status check (only for final states)
    // Don't skip if status is pending - we need to keep checking
    if (hasCheckedStatus.current === orderId && orderStatus && !loading && !isReturningFromPayment) {
      // Don't consider "failed" as final state unless it was marked as final failure
      // This allows backend to check with LiqPay API up to 5 times before giving up
      const isFinalFailed = (orderStatus.status === 'failed' || orderStatus.paymentStatus === 'failed') && 
                            !orderStatus.isPaid && 
                            orderStatus.paymentDetails?.finalFailure === true;
      const isFinalSuccess = orderStatus.isPaid || orderStatus.status === 'completed' || (orderStatus.confirmationNumber && orderStatus.emailSent);
      
      // Don't skip if status is pending - we need to keep checking for payment completion
      const isPending = orderStatus.status === 'pending' || orderStatus.paymentStatus === 'pending' || orderStatus.paymentStatus === 'in-progress';
      
      // Only skip if it's a final state AND polling is already active AND not pending
      if ((isFinalFailed || isFinalSuccess) && pollingIntervalRef.current && !isPending) {
        return;
      }
      // If it's a final state but no polling, still check once to ensure state is correct
      // If it's not a final state or is pending, continue to check status
      // For "failed" status without API confirmation, continue checking
    }
    
    // Only set loading to true if we don't have orderStatus yet
    // If we already have orderStatus, keep loading false to show actual status (avoid blinking)
    if ((isReturningFromPayment || isNewPaymentAttempt) && !orderStatus) {
      setLoading(true);
    } else if (orderStatus) {
      // If we have orderStatus, ensure loading is false (don't show spinner)
      setLoading(false);
    }
    
    // Only clear polling if we're starting a new check (new orderId or returning from payment)
    // Don't clear if we're just re-rendering with the same orderId
    if (isReturningFromPayment || isNewPaymentAttempt) {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
        isPollingRef.current = false;
      }
    }
    
    if (checkStatusAbortController.current) {
      checkStatusAbortController.current.abort();
    }
    
    checkStatusAbortController.current = new AbortController();
    isCheckingStatus.current = false;

    const checkPaymentStatus = async () => {
      if (isCheckingStatus.current) {
        return;
      }
      
      // Don't show loading spinner during polling if we already have orderStatus
      // Only show on first check
      const isFirstCheck = !orderStatus || !hasCheckedStatus.current;
      if (!isFirstCheck) {
        // During polling, ensure loading is false (don't show spinner)
        setLoading(false);
      }
      
      isCheckingStatus.current = true;
      
      try {
        const response = await fetch(`${API_CONFIG.baseURL}/api/orders/payment-status/${orderId}`, {
          signal: checkStatusAbortController.current?.signal
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || 'Failed to fetch order status');
        }

        const result = await response.json();
        const orderData = result.data;
        
        // Check if status actually changed before updating state
        const statusChanged = !orderStatus || 
          orderStatus.status !== orderData.status ||
          orderStatus.paymentStatus !== orderData.paymentStatus ||
          orderStatus.isPaid !== orderData.isPaid ||
          orderStatus.confirmationNumber !== orderData.confirmationNumber ||
          orderStatus.emailSent !== orderData.emailSent ||
          orderStatus.orderIdSKIDATA !== orderData.orderIdSKIDATA;
        
        // Always update orderStatus to ensure we have the latest data
        // This is important for showing the correct status instead of loading
        setOrderStatus(orderData);
        
        // Always hide loading after we get orderStatus
        // This ensures we show the actual status instead of loading spinner
        setLoading(false);
        
        // Don't set hasCheckedStatus if we're returning from payment or status is still pending
        // This ensures we continue checking status until we get final status
        const isStillPending = orderData.paymentStatus === 'pending' || orderData.paymentStatus === 'in-progress' || 
                               orderData.status === 'pending' || orderData.status === 'in-progress';
        const isNotPaid = !orderData.isPaid;
        
        if (!isReturningFromPaymentRef.current && !isStillPending && !isNotPaid) {
          // Only set hasCheckedStatus if this is not a fresh retry (paymentStatusCheckAttempts === 0)
          // This allows polling to continue after retry
          const isFreshRetry = orderData.paymentStatusCheckAttempts === 0 || orderData.paymentStatusCheckAttempts === undefined;
          if (!isFreshRetry) {
            hasCheckedStatus.current = orderId;
          }
        }
        lastCheckedOrderId.current = orderId;
        
        // Reset isReturningFromPaymentRef only after we get final status (success or final failure)
        // This ensures we continue checking if status is still pending
        // But only reset if we actually got a status update (not just a re-check)
        const isFinalStatus = (orderData.isPaid && orderData.paymentStatus === 'success') || 
                             (orderData.paymentStatus === 'failed' && orderData.paymentDetails?.finalFailure === true);
        const hasStatusChanged = !orderStatus || 
                                orderStatus.status !== orderData.status ||
                                orderStatus.paymentStatus !== orderData.paymentStatus ||
                                orderStatus.isPaid !== orderData.isPaid;
        
        // Only reset if we got final status AND status actually changed
        // This prevents resetting too early when status hasn't updated yet
        if (isReturningFromPaymentRef.current && isFinalStatus && hasStatusChanged) {
          isReturningFromPaymentRef.current = false;
        }
        
        const hasConfirmationNumber = orderData.confirmationNumber && orderData.confirmationNumber.trim() !== '';
        // Don't consider "failed" as final state - it might update after LiqPay API check
        // Only consider it final if payment was explicitly confirmed as failed AND marked as final failure
        // This allows backend to check up to 5 times before giving up
        const isFinalFailed = (orderData.status === 'failed' || orderData.paymentStatus === 'failed') && 
                              !orderData.isPaid && 
                              orderData.paymentDetails?.finalFailure === true;
        const isFinalSuccess = orderData.status === 'completed' || 
                              (orderData.isPaid && hasConfirmationNumber) ||
                              (orderData.emailSent === true && hasConfirmationNumber);
        const isFinalState = isFinalFailed || isFinalSuccess;
        
        if (isFinalState) {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
            isPollingRef.current = false;
          }
          return;
        }
        
        // Continue polling for "failed" or "pending" status to allow backend to check with LiqPay API
        // This is important for production where status might update after API check
        // Also poll for pending status to check for payment completion
        
        // Always start polling if status is not final (pending, failed without finalFailure, etc.)
        if (!isPollingRef.current && !pollingIntervalRef.current) {
          isPollingRef.current = true;
          pollingIntervalRef.current = setInterval(() => {
            if (!isCheckingStatus.current && isPollingRef.current) {
              checkPaymentStatus();
            }
          }, 5000);
        }

      } catch (err) {
        if (err.name === 'AbortError') {
          return;
        }
        setError(err.message);
        setLoading(false);
        
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
          isPollingRef.current = false;
        }
      } finally {
        isCheckingStatus.current = false;
      }
    };

    // Store checkPaymentStatus in ref so it can be called from outside useEffect
    checkPaymentStatusRef.current = checkPaymentStatus;

    checkPaymentStatus();
    
    return () => {
      if (checkStatusAbortController.current) {
        checkStatusAbortController.current.abort();
      }
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
        isPollingRef.current = false;
      }
      isCheckingStatus.current = false;
    };
  }, [orderId, searchParams, retryTrigger, propOrderId]);

  const getStatusMessage = () => {
    if (!orderStatus) return null;

    // If we're returning from payment and status is still pending, show checking message
    // This provides better UX during the delay between closing window and getting updated status
    if (isReturningFromPaymentRef.current && 
        (orderStatus.paymentStatus === 'pending' || orderStatus.paymentStatus === 'in-progress' || 
         orderStatus.status === 'pending' || orderStatus.status === 'in-progress')) {
      return {
        type: 'warning',
        title: '⏳ Перевірка оплати',
        message: 'Перевіряємо статус оплати після повернення. Будь ласка, зачекайте...',
        color: '#f59e0b'
      };
    }

    if (orderStatus.isPaid === true || orderStatus.paymentStatus === 'success') {
      if (orderStatus.emailSent === true && orderStatus.confirmationNumber) {
        return {
          type: 'success',
          title: '🎿 Замовлення готове!',
          message: 'Ваш квиток готовий! Перевірте електронну пошту для отримання деталей.',
          color: '#22c55e'
        };
      }
      if (orderStatus.confirmationNumber) {
        return {
          type: 'success',
          title: '🎿 Замовлення готове!',
          message: 'Ваш квиток готовий! Перевірте електронну пошту для отримання деталей.',
          color: '#22c55e'
        };
      }
      return {
        type: 'success',
        title: '✅ Оплата успішна!',
        message: 'Ваше замовлення успішно оплачено. Ми обробляємо ваш квиток.',
        color: '#22c55e'
      };
    }

    // Check for pending/in-progress status first - don't show error for these
    // Always show "Перевірка оплати" for pending status to continue polling
    if (orderStatus.paymentStatus === 'pending' || orderStatus.paymentStatus === 'in-progress' || 
        orderStatus.status === 'pending' || orderStatus.status === 'in-progress') {
      return {
        type: 'warning',
        title: '⏳ Перевірка оплати',
        message: 'Перевіряємо статус оплати. Будь ласка, зачекайте...',
        color: '#f59e0b'
      };
    }

    // Check for failed status - only show error if it's final failure
    // Otherwise show "Перевірка оплати" to allow backend to retry up to 5 times
    if ((orderStatus.paymentStatus === 'failed' || orderStatus.status === 'failed') && !orderStatus.isPaid) {
      // Only show error if it's explicitly marked as final failure
      // This allows backend to check with LiqPay API up to 5 times before giving up
      const isFinalFailed = orderStatus.paymentDetails?.finalFailure === true;
      
      if (isFinalFailed) {
        return {
          type: 'error',
          title: '❌ Помилка оплати',
          message: 'Оплата не була завершена. Спробуйте ще раз або зверніться до служби підтримки.',
          color: '#ef4444'
        };
      }
      
      // If failed but not final, show as checking (backend will retry up to 5 times)
      return {
        type: 'warning',
        title: '⏳ Перевірка оплати',
        message: 'Перевіряємо статус оплати. Будь ласка, зачекайте...',
        color: '#f59e0b'
      };
    }

    switch (orderStatus.status) {
      case 'paid':
        return {
          type: 'success',
          title: '✅ Оплата успішна!',
          message: 'Ваше замовлення успішно оплачено. Ми обробляємо ваш квиток.',
          color: '#22c55e'
        };
      case 'completed':
        return {
          type: 'success',
          title: '🎿 Замовлення готове!',
          message: 'Ваш квиток готовий! Перевірте електронну пошту для отримання деталей.',
          color: '#22c55e'
        };
      case 'pending':
        return {
          type: 'warning',
          title: '⏳ Перевірка оплати',
          message: 'Перевіряємо статус оплати. Будь ласка, зачекайте...',
          color: '#f59e0b'
        };
      default:
        // For unknown statuses, show info instead of error
        return {
          type: 'info',
          title: '📋 Перевірка статусу',
          message: 'Перевіряємо статус вашого замовлення...',
          color: '#3b82f6'
        };
    }
  };

  const handleReturnHome = () => {
    if (onReturnHome) {
      onReturnHome();
      return;
    }
    
    if (orderId) {
      try {
        if (isInPopup) {
          if (window.opener && window.opener.parent && window.opener.parent !== window.opener) {
            window.opener.parent.postMessage({
              type: 'show-confirmation',
              orderId: orderId
            }, '*');
          } else if (window.opener) {
            window.opener.postMessage({
              type: 'show-confirmation',
              orderId: orderId
            }, '*');
          }
        } else {
          window.postMessage({
            type: 'show-confirmation',
            orderId: orderId
          }, '*');
        }
      } catch (e) {
        console.warn('Failed to send postMessage:', e);
      }
      
      if (isInPopup) {
        requestAnimationFrame(() => {
          try {
            window.close();
          } catch (e) {
            console.warn('Failed to close popup window:', e);
          }
        });
      } else {
        localStorage.removeItem('currentOrderId');
        localStorage.removeItem('orderData');
        window.location.href = '/';
      }
    } else {
      localStorage.removeItem('currentOrderId');
      localStorage.removeItem('orderData');
      window.location.href = '/';
    }
  };

  const handleRetryPayment = async () => {
    const isInIframe = window.self !== window.top;
    let paymentWindow = null;
    
    paymentWindow = window.open(
      'about:blank',
      'liqpay-checkout',
      'width=800,height=600,scrollbars=yes,resizable=yes'
    );
    
    try {
      let orderIdToFetch = null;
      
      if (orderStatus && orderStatus.orderId) {
        orderIdToFetch = orderStatus.orderId;
      } else {
        const orderDataStr = localStorage.getItem('orderData');
        if (orderDataStr) {
          const orderData = JSON.parse(orderDataStr);
          orderIdToFetch = orderData.orderId;
        }
      }
      
      if (!orderIdToFetch) {
        orderIdToFetch = orderId;
      }
      
      if (!orderIdToFetch) {
        handleReturnHome();
        return;
      }
      
      const orderResponse = await fetch(`${API_CONFIG.baseURL}/api/orders/payment-status/${orderIdToFetch}`);
      
      if (!orderResponse.ok) {
        throw new Error('Failed to fetch order data');
      }
      
      const orderResult = await orderResponse.json();
      const fullOrderData = orderResult.data;
      
      if (!fullOrderData) {
        throw new Error('Order data not found');
      }
      
      const isInIframe = window.self !== window.top;
      let parentUrl = null;
      if (isInIframe) {
        try {
          parentUrl = window.top.location.href;
        } catch (e) {
          if (document.referrer) {
            parentUrl = document.referrer;
          }
        }
      }
      
      const sourceData = fullOrderData;
      
      let customerInfo = sourceData.customerInfo || sourceData.customer || {};
      let paymentInfo = sourceData.paymentInfo || {};
      
      let localStorageOrderData = null;
      if (!customerInfo || Object.keys(customerInfo).length === 0) {
        const orderDataStr = localStorage.getItem('orderData');
        if (orderDataStr) {
          try {
            localStorageOrderData = JSON.parse(orderDataStr);
            customerInfo = localStorageOrderData.customerInfo || localStorageOrderData.customer || customerInfo;
            paymentInfo = localStorageOrderData.paymentInfo || paymentInfo;
          } catch (e) {
            console.warn('Failed to parse orderData from localStorage:', e);
          }
        }
      }
      
      const amount = paymentInfo.amount || sourceData.totalPrice || sourceData.amount || sourceData.calculatedPrice || 0;
      
      const firstName = customerInfo.firstName || '';
      const lastName = customerInfo.lastName || '';
      const customerName = `${firstName} ${lastName}`.trim() || 'Customer';
      const customerEmail = customerInfo.email || '';
      
      const orderIdToUse = sourceData.orderId || (localStorageOrderData && localStorageOrderData.orderId) || orderIdToFetch;
      if (!orderIdToUse) {
        throw new Error('Order ID is required for payment retry');
      }
      
      if (!customerEmail || customerEmail.trim() === '') {
        throw new Error('Email is required for payment retry');
      }
      
      if (!amount || amount <= 0) {
        throw new Error('Payment amount is required and must be greater than 0');
      }
      
      if (!customerName || customerName.trim() === '') {
        throw new Error('Customer name is required for payment retry');
      }
      
      // Reset polling state before retry to restart status checking
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
        isPollingRef.current = false;
      }
      
      if (checkStatusAbortController.current) {
        checkStatusAbortController.current.abort();
      }
      
      // Reset status checking state
      hasCheckedStatus.current = null;
      lastCheckedOrderId.current = null;
      isCheckingStatus.current = false;
      
      // Reset order status to trigger new polling
      // Set status to pending optimistically to show correct message immediately
      setOrderStatus(prev => prev ? {
        ...prev,
        status: 'pending',
        paymentStatus: 'pending',
        paymentStatusCheckAttempts: 0,
        paymentDetails: {
          ...prev.paymentDetails,
          finalFailure: false
        }
      } : null);
      setError(null);
      setLoading(false);
      
      // Trigger useEffect to restart polling by updating retryTrigger state
      // This ensures useEffect runs again and restarts polling properly
      setRetryTrigger(prev => prev + 1);
      
      // Also manually restart polling after a short delay as backup
      setTimeout(() => {
        // Clear hasCheckedStatus to force fresh check
        hasCheckedStatus.current = null;
        lastCheckedOrderId.current = null;
        isCheckingStatus.current = false;
        
        // Ensure polling is stopped before restarting
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
          isPollingRef.current = false;
        }
        
        if (checkPaymentStatusRef.current) {
          checkPaymentStatusRef.current();
        }
      }, 500);
      
      const checkoutPayload = {
        orderId: orderIdToUse,
        amount: amount,
        currency: paymentInfo.currency || 'UAH',
        description: `Ski Pass Purchase - Retry Payment`,
        customerName: customerName,
        customerFirstName: firstName,
        customerLastName: lastName,
        customerEmail: customerEmail,
        customerPhone: customerInfo.phone || '',
        isInIframe: isInIframe,
        parentUrl: parentUrl
      };
      
      const checkoutUrl = canSellParam === 'false' 
        ? `${API_CONFIG.baseURL}/api/orders/liqpay-checkout?can_sell=false`
        : `${API_CONFIG.baseURL}/api/orders/liqpay-checkout`;
      const response = await fetch(checkoutUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(checkoutPayload)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || 'Failed to create checkout URL');
      }
      
      const checkoutResult = await response.json();
      
      if (!checkoutResult.success) {
        throw new Error(checkoutResult.message || checkoutResult.error || 'Failed to create checkout URL');
      }
      
      if (checkoutResult.data && checkoutResult.data.url) {
        if (paymentWindow) {
          try {
            paymentWindow.location.href = checkoutResult.data.url;
          } catch (e) {
            if (paymentWindow.closed) {
              paymentWindow = window.open(
                checkoutResult.data.url,
                'liqpay-checkout',
                'width=800,height=600,scrollbars=yes,resizable=yes'
              );
            } else {
              paymentWindow.location.replace(checkoutResult.data.url);
            }
          }
        }
        
        if (!paymentWindow || paymentWindow.closed) {
          // Popup blocked - fallback to redirect
          if (isInIframe) {
            try {
              window.parent.postMessage({
                type: 'liqpay-checkout',
                url: checkoutResult.data.url
              }, '*');
            } catch (e) {
              window.location.href = checkoutResult.data.url;
            }
          } else {
            window.location.href = checkoutResult.data.url;
          }
        } else {
            const checkPaymentWindow = setInterval(() => {
              if (paymentWindow.closed) {
                clearInterval(checkPaymentWindow);
                // Mark as returning from payment to force fresh status check
                isReturningFromPaymentRef.current = true;
                
                // Reset polling state when returning from payment
                if (pollingIntervalRef.current) {
                  clearInterval(pollingIntervalRef.current);
                  pollingIntervalRef.current = null;
                  isPollingRef.current = false;
                }
                
                if (checkStatusAbortController.current) {
                  checkStatusAbortController.current.abort();
                }
                
                // Clear status to force fresh check and restart polling
                hasCheckedStatus.current = null;
                lastCheckedOrderId.current = null;
                isCheckingStatus.current = false;
                
                // Don't reset orderStatus to null - keep previous status to show user-friendly message
                // This prevents showing confusing form/loading state
                // Only set loading if we don't have orderStatus yet
                if (!orderStatus) {
                  setLoading(true);
                } else {
                  setLoading(false);
                }
                
                // Trigger useEffect to restart polling by updating retryTrigger
                setRetryTrigger(prev => prev + 1);
                
                // Check status immediately without delay
                setTimeout(() => {
                  if (checkPaymentStatusRef.current) {
                    checkPaymentStatusRef.current();
                  }
                }, 100);
                
                if (isInIframe) {
                  window.parent.postMessage({
                    type: 'show-confirmation',
                    orderId: orderIdToUse
                  }, '*');
                } else {
                  window.opener?.postMessage({
                    type: 'show-confirmation',
                    orderId: orderIdToUse
                  }, '*');
                }
              }
            }, 1000);
            
            const messageHandler = (event) => {
              if (event.data && event.data.type === 'show-confirmation' && event.data.orderId) {
                clearInterval(checkPaymentWindow);
                window.removeEventListener('message', messageHandler);
                
                // Mark as returning from payment to force fresh status check
                isReturningFromPaymentRef.current = true;
                
                // Reset polling state when returning from payment
                if (pollingIntervalRef.current) {
                  clearInterval(pollingIntervalRef.current);
                  pollingIntervalRef.current = null;
                  isPollingRef.current = false;
                }
                
                if (checkStatusAbortController.current) {
                  checkStatusAbortController.current.abort();
                }
                
                hasCheckedStatus.current = null;
                lastCheckedOrderId.current = null;
                isCheckingStatus.current = false;
                
                // Don't reset orderStatus to null - keep previous status to show user-friendly message
                // This prevents showing confusing form/loading state
                // Only set loading if we don't have orderStatus yet
                if (!orderStatus) {
                  setLoading(true);
                } else {
                  setLoading(false);
                }
                
                // Trigger useEffect to restart polling by updating retryTrigger
                setRetryTrigger(prev => prev + 1);
                
                // Check status immediately without delay
                setTimeout(() => {
                  if (checkPaymentStatusRef.current) {
                    checkPaymentStatusRef.current();
                  }
                }, 100);
                
                // Show confirmation is handled by PricingPageSimple message handler
              }
            };
            
            window.addEventListener('message', messageHandler);
            
            setTimeout(() => {
              clearInterval(checkPaymentWindow);
              window.removeEventListener('message', messageHandler);
            }, 30 * 60 * 1000);
          }
      } else {
        throw new Error('Payment URL not generated in response');
      }
    } catch (error) {
      const errorMessage = error.message || 'Помилка при повторній спробі оплати';
      alert(`${errorMessage}. Спробуйте пізніше або зверніться до служби підтримки.`);
    }
  };

  if (isInPopup) {
    return null;
  }

  // Don't show loading spinner if we already have orderStatus
  // This allows showing the actual status (e.g., "pending") instead of loading
  if (loading && !orderStatus) {
    return (
      <>
        <div className="skipass-payment-result-container">
          <div className="payment-result-card skipass-loading">
            <div className="skipass-loading-spinner"></div>
            <h2>Перевіряємо статус оплати...</h2>
          </div>
        </div>
      <style>{`
        .payment-result-container {
          ${embedded ? `
            position: relative;
            min-height: auto;
            height: auto;
            background: transparent;
            padding: 2rem 0;
          ` : `
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            min-height: 100vh;
            background: #f8f9fa;
            padding: 2rem;
          `}
          display: flex;
          align-items: center;
          justify-content: center;
          box-sizing: border-box;
        }
          .payment-result-card.loading {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 300px;
            height: auto;
            padding: 3rem;
            background: white;
            border-radius: 12px;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
            max-width: 500px;
            width: 100%;
            text-align: center;
          }
          .payment-result-card.loading .loading-spinner {
            width: 40px;
            height: 40px;
            border: 4px solid #e5e7eb;
            border-top: 4px solid #3b82f6;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin: 0;
            flex-shrink: 0;
          }
          .payment-result-card.loading h2 {
            margin-top: 1.5rem;
            margin-bottom: 0;
            text-align: center;
            color: #374151;
            font-size: 1.25rem;
            font-weight: 500;
          }
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </>
    );
  }

  if (error) {
    return (
      <>
        <div className="skipass-payment-result-container">
          <div className="payment-result-card skipass-error">
            <h2>❌ Помилка</h2>
            <p>{error}</p>
            <button onClick={handleReturnHome} className="skipass-btn-primary">
              Повернутися на головну
            </button>
          </div>
        </div>
      <style>{`
        .skipass-payment-result-container {
          ${embedded ? `
            position: relative;
            min-height: auto;
            height: auto;
            background: transparent;
            padding: 2rem 0;
          ` : `
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            min-height: 100vh;
            background: #f8f9fa;
            padding: 2rem;
          `}
          display: flex;
          align-items: center;
          justify-content: center;
          box-sizing: border-box;
        }
          .skipass-payment-result-card {
            background: white;
            border-radius: 12px;
            padding: 3rem;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
            max-width: 500px;
            width: 100%;
            text-align: center;
          }
          .skipass-payment-result-card.error {
            border-left: 4px solid #ef4444;
          }
          .skipass-btn-primary {
            padding: 0.75rem 1.5rem;
            border: none;
            border-radius: 8px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s;
            background: #3b82f6;
            color: white;
          }
          .skipass-btn-primary:hover {
            background: #2563eb;
          }
        `}</style>
      </>
    );
  }

  let statusInfo = getStatusMessage();

  // If statusInfo is null AND we don't have orderStatus yet, show loading state
  // Don't show loading if we already have orderStatus (even if statusInfo is null temporarily)
  if (!statusInfo && !orderStatus && loading) {
    return (
      <>
        <div className="skipass-payment-result-container">
          <div className="skipass-payment-result-card skipass-loading">
            <div className="skipass-loading-spinner"></div>
            <h2>Перевіряємо статус оплати...</h2>
          </div>
        </div>
      <style>{`
        .payment-result-container {
          ${embedded ? `
            position: relative;
            min-height: auto;
            height: auto;
            background: transparent;
            padding: 2rem 0;
          ` : `
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            min-height: 100vh;
            background: #f8f9fa;
            padding: 2rem;
          `}
          display: flex;
          align-items: center;
          justify-content: center;
          box-sizing: border-box;
        }
          .payment-result-card.loading {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 300px;
            height: auto;
            padding: 3rem;
            background: white;
            border-radius: 12px;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
            max-width: 500px;
            width: 100%;
            text-align: center;
          }
          .payment-result-card.loading .loading-spinner {
            width: 40px;
            height: 40px;
            border: 4px solid #e5e7eb;
            border-top: 4px solid #3b82f6;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin-bottom: 1rem;
          }
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </>
    );
  }

  // If statusInfo is null but we have orderStatus, show a default message
  if (!statusInfo && orderStatus) {
    statusInfo = {
      type: 'info',
      title: '📋 Перевірка статусу',
      message: 'Перевіряємо статус вашого замовлення...',
      color: '#3b82f6'
    };
  }

  // If still no statusInfo, return null (shouldn't happen, but safety check)
  if (!statusInfo) {
    return (
      <>
        <div className="skipass-payment-result-container">
          <div className="skipass-payment-result-card skipass-loading">
            <div className="skipass-loading-spinner"></div>
            <h2>Перевіряємо статус оплати...</h2>
          </div>
        </div>
      <style>{`
        .skipass-payment-result-container {
          ${embedded ? `
            position: relative;
            min-height: auto;
            height: auto;
            background: transparent;
            padding: 2rem 0;
          ` : `
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            min-height: 100vh;
            background: #f8f9fa;
            padding: 2rem;
          `}
          display: flex;
          align-items: center;
          justify-content: center;
          box-sizing: border-box;
        }
          .skipass-payment-result-card.skipass-loading {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 300px;
            height: auto;
            padding: 3rem;
            background: white;
            border-radius: 12px;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
            max-width: 500px;
            width: 100%;
            text-align: center;
          }
          .skipass-payment-result-card.skipass-loading .skipass-loading-spinner {
            width: 40px;
            height: 40px;
            border: 4px solid #e5e7eb;
            border-top: 4px solid #3b82f6;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin: 0;
            flex-shrink: 0;
          }
          .skipass-payment-result-card.loading h2 {
            margin-top: 1.5rem;
            margin-bottom: 0;
            text-align: center;
            color: #374151;
            font-size: 1.25rem;
            font-weight: 500;
          }
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </>
    );
  }

  return (
    <div className="skipass-payment-result-container">
      <div className="skipass-payment-result-card">
        <div className="skipass-status-icon" style={{ color: statusInfo.color }}>
          <h1>{statusInfo.title}</h1>
        </div>
        
        <div className="skipass-order-details">
          <h3>Деталі замовлення</h3>
          <div className="skipass-detail-row">
            <span>Номер замовлення:</span>
            <strong>{orderStatus.orderId}</strong>
          </div>
          <div className="skipass-detail-row">
            <span>Статус:</span>
            <strong style={{ 
              color: (orderStatus.status === 'failed' || orderStatus.paymentStatus === 'failed') && !orderStatus.isPaid 
                ? '#ef4444' 
                : statusInfo.color 
            }}>
              {orderStatus.isPaid ? 'Оплачено' : 
               orderStatus.paymentStatus === 'success' ? 'Оплачено' :
               orderStatus.status === 'completed' ? 'Завершено' :
               orderStatus.status === 'failed' || orderStatus.paymentStatus === 'failed' ? 'Помилка' :
               orderStatus.status === 'pending' || orderStatus.paymentStatus === 'pending' ? 'Очікування' :
               orderStatus.status === 'in-progress' || orderStatus.paymentStatus === 'in-progress' ? 'Очікування' :
               orderStatus.status || 'Очікування'}
            </strong>
          </div>
          {orderStatus.is_paid && (
            <div className="skipass-detail-row">
              <span>Оплачено:</span>
              <strong style={{ color: '#22c55e' }}>
                {new Date(orderStatus.paidAt).toLocaleString('uk-UA')}
              </strong>
            </div>
          )}
          {orderStatus.paymentDetails && (
            <div className="skipass-detail-row">
              <span>ID транзакції:</span>
              <strong>{orderStatus.paymentDetails.transactionId}</strong>
            </div>
          )}
          {orderStatus.paymentInfo && (orderStatus.paymentInfo.amount != null || orderStatus.paymentInfo.amount === 0) && (
            <div className="skipass-result-summary" style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid rgba(255,255,255,0.15)' }}>
              {orderStatus.discountPercent != null && orderStatus.discountPercent > 0 && orderStatus.originalAmount != null ? (
                <>
                  <div className="skipass-detail-row skipass-result-row" style={{ marginBottom: '0.25rem' }}>
                    <span>Проміжний підсумок:</span>
                    <strong>{Number(orderStatus.originalAmount).toFixed(2)} {orderStatus.paymentInfo.currency || 'грн'}</strong>
                  </div>
                  <div className="skipass-detail-row skipass-result-row" style={{ marginBottom: '0.25rem', color: '#86efac' }}>
                    <span>Партнерська знижка (Softserve) -{orderStatus.discountPercent}%:</span>
                    <strong>-{(Number(orderStatus.originalAmount) - Number(orderStatus.paymentInfo.amount)).toFixed(2)} {orderStatus.paymentInfo.currency || 'грн'}</strong>
                  </div>
                  <div className="skipass-detail-row skipass-result-row">
                    <span>Загальна сума:</span>
                    <strong>{Number(orderStatus.paymentInfo.amount).toFixed(2)} {orderStatus.paymentInfo.currency || 'грн'}</strong>
                  </div>
                </>
              ) : (
                <div className="skipass-detail-row skipass-result-row">
                  <span>Загальна сума:</span>
                  <strong>{Number(orderStatus.paymentInfo.amount).toFixed(2)} {orderStatus.paymentInfo.currency || 'грн'}</strong>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="skipass-status-message">
          <p>{statusInfo.message}</p>
        </div>

        {/* Messenger Instructions - Show only for successful payments and if messengers are configured */}
        {(orderStatus.isPaid === true || orderStatus.paymentStatus === 'success') && 
         orderStatus.confirmationNumber && 
         messengerConfig && 
         Object.keys(messengerConfig).length > 0 && (
          <div className="skipass-messenger-instructions">
            <h4>📱 Як отримувати Скіпаси в Телеграмі:</h4>
            
            <div className="skipass-messenger-options">
              {/* Telegram Instructions - Only if configured */}
              {messengerConfig.telegram && (
                <div className="skipass-messenger-option skipass-telegram">
                  <div className="skipass-messenger-content">
                    {messengerConfig.telegram.botLink ? (
                      <>
                        <ol>
                          <li style={{ listStyle: 'auto' }}>
                            Натисніть цю кнопку: <a 
                              href={messengerConfig.telegram.directLink || messengerConfig.telegram.botLink} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="skipass-messenger-link-btn skipass-telegram-btn"
                              style={{
                                padding: '0.5rem 1rem',
                                fontSize: '0.85rem',
                                whiteSpace: 'nowrap',
                                marginLeft: '0.5rem'
                              }}
                            >
                              🔗 Відкрити Telegram бота
                            </a>
                          </li>
                          <li style={{ listStyle: 'auto' }}>Натисніть кнопку "Start"</li>
                          <li style={{ listStyle: 'auto' }}>Натисніть кнопку "Поділітися номером телефону"</li>
                        </ol>
                      </>
                    ) : (
                      <ol>
                        <li style={{ listStyle: 'auto' }}>Відкрийте Telegram</li>
                        <li style={{ listStyle: 'auto' }}>Натисніть кнопку "Start" або надішліть <code>/start</code></li>
                        <li style={{ listStyle: 'auto' }}>Поділіться номером телефону або надішліть: <code>/link +ваш_номер</code></li>
                      </ol>
                    )}
                  </div>
                </div>
              )}

              {/* Viber Business Chat Instructions - Only if configured */}
              {messengerConfig.viberBusiness && (() => {
                const vb = messengerConfig.viberBusiness;
                const viberLink = vb.businessChatUri
                  ? `viber://pa?chatURI=${encodeURIComponent(vb.businessChatUri)}`
                  : (vb.phoneNumber ? `viber://chat?number=${String(vb.phoneNumber || '').replace(/\D/g, '')}` : null);
                const viberButtonText = vb.businessChatName
                  ? `💼 ${(vb.openChatWithName || 'Відкрити чат: {name}').replace('{name}', vb.businessChatName)}`
                  : `💼 ${vb.openViberBusinessChat || 'Відкрити Viber Business Chat'}`;
                return (
                <>
                  <h3 style={{
                    margin: '0',
                    color: 'rgb(102, 92, 172)',
                    fontSize: '1.5rem',
                    fontWeight: '600',
                    lineHeight: '1.3'
                  }}>в Viber:</h3>
                  <div className="skipass-messenger-option skipass-viber-business">
                  <div className="skipass-messenger-content">
                    {viberLink ? (
                      <>
                        <ol>
                          <li style={{ listStyle: 'auto' }}>
                            Натисніть цю кнопку: <a 
                              href={viberLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="skipass-messenger-link-btn skipass-viber-business-btn"
                              style={{
                                padding: '0.5rem 1rem',
                                fontSize: '0.85rem',
                                whiteSpace: 'nowrap',
                                marginLeft: '0.5rem'
                              }}
                            >
                              {viberButtonText}
                            </a>
                          </li>
                        </ol>
                      </>
                    ) : (
                      <p style={{ margin: 0, fontSize: '0.9rem' }}>
                        {(vb.openBusinessChatsAndFind || 'Відкрийте бізнес-чати та знайдіть «{name}».').replace('{name}', vb.senderName || 'бізнес-чат')}
                      </p>
                    )}
                  </div>
                </div>
                </>
                );
              })()}
            </div>

            <div className="skipass-messenger-note">
              <p>💡 Після підключення ви будете автоматично отримувати QR-коди на всі ваші замовлення!</p>
            </div>
          </div>
        )}

        <div className="skipass-action-buttons">
          {(orderStatus.status === 'failed' || orderStatus.paymentStatus === 'failed') && 
           !orderStatus.isPaid && (
            <button onClick={handleRetryPayment} className="skipass-btn-retry">
              Спробувати ще раз
            </button>
          )}
          <button onClick={handleReturnHome} className="skipass-btn-primary">
            Повернутися до вибору
          </button>
        </div>
      </div>

      <style>{`
        .skipass-payment-result-container {
          ${embedded ? `
          position: relative;
          min-height: auto;
          display: flex;
          flex-direction: column;
          background: #f8f9fa;
          padding: 2rem;
          box-sizing: border-box;
          ` : `
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f8f9fa;
          padding: 2rem;
          box-sizing: border-box;
          `}
        }

        .skipass-payment-result-card {
          background: white;
          border-radius: 12px;
          padding: 1rem;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          ${embedded ? `
          max-width: 500px;
          margin: 0 auto;
          ` : `
          max-width: 500px;
          `}
          width: 100%;
          text-align: center;
          display: flex;
          flex-direction: column;
        }

        .skipass-payment-result-card.error {
          border-left: 4px solid #ef4444;
        }

        .skipass-status-icon h1 {
          margin: 0 0 1rem 0;
          font-size: 1.5rem;
        }

        .skipass-order-details {
          margin: 2rem 0;
          text-align: left;
        }

        .skipass-order-details h3 {
          margin: 0 0 1rem 0;
          color: #374151;
          text-align: center;
        }

        .skipass-detail-row {
          display: flex;
          justify-content: space-between;
          margin: 0.5rem 0;
          padding: 0.5rem 0;
          border-bottom: 1px solid #e5e7eb;
        }

        .skipass-detail-row:last-child {
          border-bottom: none;
        }

        /* Result summary: stack rows vertically, prevent label/value overlap on desktop and mobile */
        .skipass-result-summary {
          display: flex;
          flex-direction: column;
          gap: 0;
        }

        .skipass-result-summary .skipass-result-row {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 0.5rem;
          flex-wrap: wrap;
          margin: 0;
          padding: 0.25rem 0;
          border-bottom: 1px solid #e5e7eb;
          min-width: 0;
        }

        .skipass-result-summary .skipass-result-row:last-child {
          border-bottom: none;
        }

        .skipass-result-summary .skipass-result-row span {
          min-width: 0;
          flex: 1 1 50%;
          word-wrap: break-word;
          overflow-wrap: break-word;
        }

        .skipass-result-summary .skipass-result-row strong {
          flex-shrink: 0;
          white-space: nowrap;
        }

        .skipass-status-message {
          margin: 2rem 0;
          padding: 1rem;
          background: #f3f4f6;
          border-radius: 8px;
        }

        .skipass-status-message p {
          margin: 0;
          color: #374151;
        }

        .skipass-messenger-instructions {
          margin: 2rem 0;
          padding: 1.5rem;
          background: #f0f9ff;
          border-radius: 8px;
          border: 1px solid #bae6fd;
        }

        .skipass-messenger-instructions h4 {
          margin: 0 0 0.5rem 0;
          color: #0369a1;
          font-size: 1.1rem;
        }

        .skipass-messenger-instructions > p {
          margin: 0 0 1rem 0;
          color: #0c4a6e;
          font-size: 0.9rem;
        }

        .skipass-messenger-options {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          margin: 1rem 0;
        }

        .skipass-messenger-option {
          padding: 0.5rem;
          background: white;
          border-radius: 8px;
          border: 1px solid #e0e7ff;
        }

        .skipass-messenger-option.telegram {
          border-color: #3b82f6;
        }

        .skipass-messenger-icon {
          font-size: 2rem;
          flex-shrink: 0;
        }

        .skipass-messenger-content {
          flex: 1;
          text-align: left;
        }

        .skipass-messenger-content ol {
          margin: 0;
          padding-left: 1.25rem;
          color: #475569;
          font-size: 0.875rem;
          line-height: 1.6;
        }

        .skipass-messenger-content li {
          margin: 0.25rem 0;
        }

        .skipass-messenger-content code {
          background: #f1f5f9;
          padding: 0.125rem 0.375rem;
          border-radius: 4px;
          font-family: 'Courier New', monospace;
          font-size: 0.8rem;
          color: #0f172a;
        }

        .skipass-messenger-link-container {
          margin: 0.75rem 0;
        }

        .skipass-messenger-link-btn {
          display: inline-block;
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          text-decoration: none;
          font-weight: 600;
          font-size: 0.9rem;
          transition: all 0.2s;
          text-align: center;
          border: 2px solid;
        }

        .skipass-messenger-link-btn.telegram-btn {
          background: #0088cc;
          color: white;
          border-color: #0088cc;
        }

        .skipass-messenger-link-btn.telegram-btn:hover {
          background: #006ba3;
          border-color: #006ba3;
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(0, 136, 204, 0.3);
        }

        .skipass-messenger-option.viber-business {
          border-color: #665cac;
        }

        .skipass-messenger-link-btn.viber-business-btn {
          background: #665cac;
          color: white;
          border-color: #665cac;
        }

        .skipass-messenger-link-btn.viber-business-btn:hover {
          background: #524a8a;
          border-color: #524a8a;
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(102, 92, 172, 0.3);
        }

        .skipass-messenger-content a {
          text-decoration: none;
          word-break: break-all;
        }

        .skipass-messenger-content a:hover {
          text-decoration: underline;
        }

        .skipass-messenger-note {
          margin-top: 1rem;
          padding: 0.75rem;
          background: #fef3c7;
          border-radius: 6px;
          border-left: 3px solid #f59e0b;
        }

        .skipass-messenger-note p {
          margin: 0;
          color: #92400e;
          font-size: 0.875rem;
        }

        .skipass-action-buttons {
          display: flex;
          gap: 1rem;
          justify-content: center;
          flex-wrap: wrap;
          ${embedded ? `
          position: sticky;
          bottom: 0;
          background: white;
          padding: 1rem;
          margin: 0 -1rem -1rem -1rem;
          border-top: 1px solid #e9ecef;
          border-radius: 0 0 12px 12px;
          ` : ''}
        }

        .skipass-btn-primary, .skipass-btn-retry {
          padding: 0.75rem 1.5rem;
          border: none;
          border-radius: 8px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .skipass-btn-primary {
          background: #3b82f6;
          color: white;
        }

        .skipass-btn-primary:hover {
          background: #2563eb;
        }

        .skipass-btn-retry {
          background: #f59e0b;
          color: white;
        }

        .skipass-btn-retry:hover {
          background: #d97706;
        }

        .skipass-loading-spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #e5e7eb;
          border-top: 4px solid #3b82f6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: auto;
        }

        .skipass-payment-result-card.skipass-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 300px;
          height: auto;
          padding: 1rem;
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          max-width: 500px;
          width: 100%;
          text-align: center;
        }

        .skipass-payment-result-card.skipass-loading .loading-spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #e5e7eb;
          border-top: 4px solid #3b82f6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0;
          flex-shrink: 0;
        }

        .skipass-payment-result-card.skipass-loading h2 {
          margin-top: 1.5rem;
          margin-bottom: 0;
          text-align: center;
          color: #374151;
          font-size: 1.25rem;
          font-weight: 500;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @media (max-width: 768px) {
          .skipass-payment-result-container {
            padding: 1rem;
          }

          .skipass-payment-result-card {
            padding: 2rem;
          }

          .skipass-action-buttons {
            flex-direction: column;
          }

          /* Mobile: result summary – label and value on separate lines to prevent overlap/clipping */
          .skipass-result-summary .skipass-result-row {
            flex-direction: column;
            align-items: stretch;
            gap: 0.125rem;
            width: 100%;
            box-sizing: border-box;
          }

          .skipass-result-summary .skipass-result-row span {
            flex: none;
            max-width: 100%;
          }

          .skipass-result-summary .skipass-result-row strong {
            flex: none;
            width: 100%;
            text-align: right;
            white-space: normal;
          }
        }
      `}</style>
    </div>
  );
};

export default PaymentResult;
